import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import { getAdmins, deleteAdmin, deleteRole } from "../../actions/admin";
import UserForm from "./UserForm";
import RoleForm from "./RoleForm";
import { roleName, roleBadge, accessLabel, dateText } from "./roleLabels";
import { primaryButton, smallButton } from "../template/templateLabels";

// Who can sign in to this app with which role, and what each role opens. Full
// is fixed in the backend's utils/access.js; the other roles are added, edited
// and removed here. A changed role or a removed user takes effect on their
// next request. A user sets their own password from a link mailed to them, and
// is pending until they do.

const card = "bg-white rounded-lg shadow-sm dark:bg-gray-800";
const dangerButton =
  "inline-flex items-center px-3 py-1 text-xs font-medium leading-5 text-red-600 transition-colors duration-150 bg-white border border-red-200 rounded-lg dark:text-red-400 dark:bg-gray-700 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-gray-600 focus:outline-none";

// null: the form is closed; NEW: adding a user or a role; otherwise the admin
// or role being edited
const NEW = "new";

const Users = ({
  getAdmins,
  deleteAdmin,
  deleteRole,
  admin: { admins, roles, keys, loading },
  me,
}) => {
  const [editing, setEditing] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [busyRole, setBusyRole] = useState(null);

  useEffect(() => {
    getAdmins();
  }, [getAdmins]);

  const isSelf = (item) => Boolean(me && item && me._id === item._id);

  const remove = async (item) => {
    if (
      !window.confirm(
        `"${item.name}" хэрэглэгчийг устгах уу? Тэр даруй системээс гарна.`
      )
    )
      return;
    setBusyId(item._id);
    await deleteAdmin(item._id);
    setBusyId(null);
  };

  const countOf = (role) => admins.filter((item) => item.role === role).length;

  const removeRole = async (item) => {
    if (!window.confirm(`"${item.name}" эрхийг устгах уу?`)) return;
    setBusyRole(item.role);
    await deleteRole(item.role);
    setBusyRole(null);
  };

  // Why a role cannot be changed here, if it cannot. Changing one's own could
  // take away one's access to this page.
  const lockedText = (item) => {
    if (item.locked) return "Өөрчлөх боломжгүй";
    if (me && me.role === item.role) return "Таны эрх";
    return null;
  };

  const roleCard = (item) => {
    const count = countOf(item.role);
    const busy = busyRole === item.role;
    const locked = lockedText(item);
    return (
      <div key={item.role} className={"flex flex-col p-4 " + card}>
        <div className="flex items-center justify-between gap-2">
          <span
            className={
              "min-w-0 px-2 py-1 text-xs font-semibold leading-tight truncate rounded-full " +
              roleBadge(roles, item.role)
            }
          >
            {item.name}
          </span>
          <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
            {count} хэрэглэгч
          </span>
        </div>
        <div className="flex flex-wrap gap-1 mt-3">
          {item.access.map((key) => (
            <span
              key={key}
              className="px-2 py-0.5 text-xs text-gray-600 bg-gray-100 rounded dark:text-gray-300 dark:bg-gray-700"
            >
              {accessLabel(key)}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 pt-3 mt-auto">
          {locked ? (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              <i className="mr-1 fas fa-lock" />
              {locked}
            </span>
          ) : (
            <>
              {/* The backend refuses it too, while an admin has the role */}
              <button
                className={dangerButton + (count > 0 || busy ? " opacity-50 cursor-not-allowed" : "")}
                disabled={count > 0 || busy}
                title={count > 0 ? "Энэ эрхтэй хэрэглэгч байгаа тул устгах боломжгүй" : undefined}
                onClick={() => removeRole(item)}
              >
                <i className="mr-1.5 fas fa-trash-alt" />
                Устгах
              </button>
              <button
                className={smallButton + " inline-flex items-center" + (busy ? " opacity-50 cursor-wait" : "")}
                disabled={busy}
                onClick={() => setEditingRole(item)}
              >
                <i className="mr-1.5 fas fa-pen" />
                Засах
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const row = (item) => {
    const busy = busyId === item._id;
    const self = isSelf(item);
    return (
      <tr key={item._id} className="text-gray-700 dark:text-gray-400">
        <td className="px-4 py-3 text-sm">
          <div className="flex items-center">
            <div
              className="flex items-center justify-center flex-shrink-0 w-8 h-8 mr-3 text-sm font-semibold text-purple-600 uppercase bg-purple-100 rounded-full dark:text-purple-300 dark:bg-purple-900"
              aria-hidden="true"
            >
              {(item.name || "?").trim().charAt(0)}
            </div>
            <span className="font-semibold">{item.name}</span>
            {self && (
              <span className="px-2 py-0.5 ml-2 text-xs text-gray-600 bg-gray-100 rounded-full dark:text-gray-300 dark:bg-gray-700">
                Та
              </span>
            )}
            {item.pending && (
              <span
                className="px-2 py-0.5 ml-2 text-xs text-yellow-700 bg-yellow-100 rounded-full dark:text-yellow-100 dark:bg-yellow-700"
                title="Урилгаа нээж нууц үгээ үүсгээгүй. Үүсгэх хүртэл нэвтэрч чадахгүй."
              >
                Баталгаажаагүй
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-sm">{item.email}</td>
        <td className="px-4 py-3 text-sm">
          <span
            className={
              "px-2 py-1 text-xs font-semibold leading-tight rounded-full " +
              roleBadge(roles, item.role)
            }
          >
            {roleName(roles, item.role)}
          </span>
        </td>
        <td className="px-4 py-3 text-sm">{dateText(item.date)}</td>
        <td className="px-4 py-3 text-sm">
          <div className="flex justify-end gap-2">
            {!self && (
              <button
                className={dangerButton + (busy ? " opacity-50 cursor-wait" : "")}
                disabled={busy}
                onClick={() => remove(item)}
              >
                <i className="mr-1.5 fas fa-trash-alt" />
                Устгах
              </button>
            )}
            <button
              className={smallButton + " inline-flex items-center" + (busy ? " opacity-50 cursor-wait" : "")}
              disabled={busy}
              onClick={() => setEditing(item)}
            >
              <i className="mr-1.5 fas fa-pen" />
              Засах
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <Admin>
      {/* min-w-0: the layout's grid would otherwise widen the page to the
          table's width on a phone, instead of letting it scroll */}
      <div className="min-w-0">
        <div className="flex flex-wrap items-end justify-between gap-4 my-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-200">
              Хэрэглэгчид
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Энэ системд нэвтэрдэг хүмүүс ба тэдний эрх
            </p>
          </div>
          <button
            className={primaryButton + " inline-flex items-center whitespace-nowrap"}
            onClick={() => setEditing(NEW)}
            disabled={loading}
          >
            <i className="mr-2 fas fa-plus" />
            Шинэ хэрэглэгч
          </button>
        </div>

        {loading ? (
          <img
            src={spinner}
            className="my-6"
            style={{ width: "50px", margin: "auto", display: "block" }}
            alt="Loading..."
          />
        ) : (
          <>
            <div className="w-full mb-8 overflow-hidden rounded-lg shadow-xs">
              <div className="w-full overflow-x-auto">
                <table className="w-full whitespace-nowrap">
                  <thead>
                    <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b dark:border-gray-700 bg-gray-50 dark:text-gray-400 dark:bg-gray-800">
                      <th className="px-4 py-3">Нэр</th>
                      <th className="px-4 py-3">Имэйл</th>
                      <th className="px-4 py-3">Эрх</th>
                      <th className="px-4 py-3">Бүртгэсэн</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y dark:divide-gray-700 dark:bg-gray-800">
                    {admins.map(row)}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 mb-4">
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                Эрхүүд
              </h3>
              <button
                className={smallButton + " inline-flex items-center whitespace-nowrap"}
                onClick={() => setEditingRole(NEW)}
              >
                <i className="mr-1.5 fas fa-plus" />
                Шинэ эрх
              </button>
            </div>
            <div className="grid gap-4 mb-4 md:grid-cols-2 xl:grid-cols-3">
              {roles.map(roleCard)}
            </div>
            <p className="flex items-start mb-8 text-xs text-gray-500 dark:text-gray-400">
              <i className="mt-0.5 mr-2 fas fa-info-circle" />
              Эрхийн өөрчлөлт шууд үйлчилнэ, устгасан хэрэглэгч системээс гарна.
              Тухайн хэрэглэгчийн цэс хуудсаа дахин ачаалахад шинэчлэгдэнэ.
            </p>
          </>
        )}

        {editing && (
          <UserForm
            admin={editing === NEW ? null : editing}
            roles={roles}
            isSelf={editing !== NEW && isSelf(editing)}
            onClose={() => setEditing(null)}
          />
        )}

        {editingRole && (
          <RoleForm
            role={editingRole === NEW ? null : editingRole}
            keys={keys}
            onClose={() => setEditingRole(null)}
          />
        )}
      </div>
    </Admin>
  );
};

Users.propTypes = {
  getAdmins: PropTypes.func.isRequired,
  deleteAdmin: PropTypes.func.isRequired,
  deleteRole: PropTypes.func.isRequired,
  admin: PropTypes.object.isRequired,
  me: PropTypes.object,
};

const mapStateToProps = (state) => ({
  admin: state.admin,
  me: state.auth.user,
});

export default connect(mapStateToProps, { getAdmins, deleteAdmin, deleteRole })(Users);
